import { Middleware } from 'redux';
import config from '../../config/config';

/**
 * Redux Error Logger Middleware
 *
 * Purpose:
 * Validates Redux actions in development mode to catch common mistakes that cause
 * "Actions must be plain objects" errors. This middleware runs AFTER redux-thunk
 * to validate actions that reach the reducers.
 *
 * Why this is needed:
 * Redux requires all actions to be plain JavaScript objects with a 'type' property.
 * However, developers may accidentally dispatch:
 * - Promises from async operations
 * - Class instances or objects with custom prototypes
 * - undefined/null (when action creators have conditional returns)
 *
 * Middleware order (important):
 * [redux-thunk] → [errorLoggerMiddleware] → [reducers]
 * ↑ Handles thunks ↑ Validates plain objects
 *
 * Note: This middleware will NOT catch functions dispatched as actions because
 * redux-thunk processes them first. It only validates plain objects and other
 * non-function values that should not reach reducers.
 *
 * Active in: development, local, testDebug environments only
 */
export const errorLoggerMiddleware: Middleware = () => next => action => {
  try {
    const isDev =
      config.ENV_NAME === 'testDebug' || config.ENV_NAME === 'local' || config.ENV_NAME === 'dev';

    if (isDev) {
      /**
       * CHECK #1: Function Detection
       * Catches: Functions that somehow passed through redux-thunk
       *
       * Note: This check will rarely trigger because redux-thunk middleware runs first
       * and processes all function actions. However, it's kept as a safety net for edge cases.
       *
       * If this triggers, it means:
       * - A function was returned from a thunk and dispatched again (unusual)
       * - There's a bug in redux-thunk configuration
       * - A custom middleware created a function action
       */
      if (typeof action === 'function') {
        console.error('❌ ERROR: Function dispatched as action!');
        console.error('⚠️ Actions must be plain objects. Use custom middleware for async actions.');
        console.error('Function:', action);
        console.trace('Stack trace:');
        throw new Error('Actions must be plain objects. Use custom middleware for async actions.');
      }

      /**
       * CHECK #2: Promise Detection
       * Catches: Promises being dispatched directly
       * Example bad code:
       *   dispatch(fetch('/api/users')); // ❌ This will fail
       *
       * Why it fails: Promises are objects but not plain objects. They have .then() methods
       * and special internal slots. Redux cannot serialize them to store.
       *
       * Correct approach: Use thunk middleware to handle async operations
       *   dispatch(async (dispatch) => {
       *     const data = await fetch('/api/users');
       *     dispatch({ type: 'USERS_LOADED', payload: data });
       *   });
       */
      if (action && typeof action === 'object' && typeof action.then === 'function') {
        console.error('❌ ERROR: Promise dispatched as action!');
        console.error(
          '⚠️ Actions must be plain objects. Promises should be handled by middleware.'
        );
        console.error('Promise:', action);
        console.trace('Stack trace:');
        throw new Error('Actions must be plain objects. Use custom middleware for async actions.');
      }

      /**
       * CHECK #3: Plain Object Validation
       * Catches: Class instances, objects with custom prototypes
       * Example bad code:
       *   class UserAction { type = 'USER_ACTION'; }
       *   dispatch(new UserAction()); // ❌ This will fail
       *
       * Why it fails: Redux requires plain objects created via {} or Object.create(null).
       * Objects with custom prototypes cannot be properly serialized and may have
       * unexpected behavior in time-travel debugging.
       *
       * How it works:
       * - Plain object: Object.getPrototypeOf(obj) === Object.prototype
       * - Object.create(null): Object.getPrototypeOf(obj) === null
       * - Class instance: Object.getPrototypeOf(obj) === ClassName.prototype ❌
       *
       * Exceptions:
       * - @@INIT: Redux's internal initialization action
       * - @@redux/*: Redux's internal actions (devtools, etc.)
       */
      if (action && typeof action === 'object') {
        const isPlainObject =
          Object.getPrototypeOf(action) === Object.prototype ||
          Object.getPrototypeOf(action) === null;

        if (!isPlainObject && action.type !== '@@INIT' && !action.type?.startsWith('@@redux')) {
          console.error('❌ ERROR: Non-plain object dispatched!');
          console.error('⚠️ Actions must be plain objects.');
          console.error('Action:', action);
          console.error('Prototype:', Object.getPrototypeOf(action));
          console.trace('Stack trace:');
        }
      }

      /**
       * CHECK #4: Type Property Validation
       * Catches: Actions without required 'type' field
       * Example bad code:
       *   dispatch({ payload: userData }); // ❌ Missing 'type'
       *
       * Why it fails: Redux uses the 'type' field to determine which reducer should
       * handle the action. Without it, no reducer will process the action.
       *
       * Note: This is a WARNING, not an error, because some middleware might handle
       * actions without 'type' (though this is not recommended).
       */
      if (action && typeof action === 'object' && !action.type && !action.type?.startsWith('@@')) {
        console.warn('⚠️ WARNING: Action without "type" property:', action);
        console.trace('Stack trace:');
      }
    }

    return next(action);
  } catch (error) {
    console.error('❌ Redux middleware error:', error);
    console.error('Action that caused error:', action);
    throw error;
  }
};
